<template>
  <section class="flex align-middle justify-center sm:py-40">
    <div class="flex flex-col w-full sm:w-auto px-8 sm:px-0">
      <Card class="!bg-transparent sm:!bg-white box-shadow-none sm:box-shadow-sm sm:w-448 max-w-screen mt-20 sm:mt-0 p-24">
        <form id="request-upgrade-form" @submit.prevent="submit()">
          <h1 class="text-h1 mb-24">Free account upgrade</h1>
          <fieldset>
            <legend class="pb-16 text-body text-grey-body">
              Check if your company’s benefits include unlimited access to all of Sittercity’s features
            </legend>
            <SearchComboBox
              id="companySearchBox"
              ref="companySearchBox"
              v-model="employerNameInput"
              allow-empty-results
              :results="filteredCorporateClients"
              type="text"
              label-name="Your employer’s name"
              :initial-value="initialValue"
              class="mb-16"
              :error-text="employerErrorText"
              :status="employerStatus"
              @selected="updateValue"
              @value-updated="filterResults"
            />
            <text-field
              id="workEmail"
              v-model="workEmail"
              label-name="Work email"
              :helper-text="emailFieldHelperText"
              name="workEmail"
              type="email"
              autocomplete="email"
              class="mb-16"
              :status="workEmailStatus"
            />
          </fieldset>
          <Button class="w-full" variant="primary" value="Check eligibility" size="medium" type="submit" :loading="isLoading" />
        </form>
      </Card>
      <popup id="errorPopup" v-model="showPopup" popup-header="We couldn’t send your request" @close="closePopup">
        <p class="text-body mb-16">
          There was an error submitting your request. Try again and if the error continues,
          <a
            href="https://support.sittercity.com/hc/en-us/requests/new?ticket_form_id=5652055814427"
            class="text-grey-clickable underline"
            >contact Customer Success</a
          >
        </p>
        <Button
          id="tryAgain"
          variant="primary"
          value="Try again"
          size="medium"
          class="w-full"
          :loading="isLoading"
          @click="closePopup"
        />
      </popup>
    </div>
  </section>
</template>

<script lang="ts">
import SearchComboBox from '@/components/search_combo_box/SearchComboBox.vue';
import Button from '@/components/button/Button.vue';
import Card from '@/components/card/Card.vue';
import Popup from '@/components/popup/Popup.vue';
import TextField from '@/components/textfield/TextField.vue';
import { sendSegmentEvent } from '@/lib/segment';
import helperStatus from '@/helpers/status';
import { CORPORATE_CLIENTS_QUERY } from '@/graphql/bh_benefits/queries';
import { validateEmail } from '@/helpers/emailHelper';
import { ADD_EMPLOYER, GENERATE_CORPORATE_VERIFICATION_TOKEN } from '@/graphql/bh_benefits/mutations';
import { isOn } from '@/lib/growthbook';
import { removeItem, setItem, STORAGE_KEYS } from '@/lib/storage';
import {
  CorporateVerificationTokenResult,
  resolveCorporateVerificationTokenResult,
} from '@/apps/bhBenefits/upgrade/helpers/corporateVerificationTokenResult';

const UPGRADE_SOURCE = 'hdyhau';

export default {
  name: 'RequestUpgrade',
  components: {
    SearchComboBox,
    Button,
    Card,
    Popup,
    TextField,
  },
  data(): any {
    return {
      showPopup: false,
      corporateClients: [],
      employerNameInput: this.$route.query.name || '',
      selectedCompanySlug: this.$route.query.slug,
      employerStatus: helperStatus.Neutral,
      filteredCorporateClients: [],
      workEmail: '',
      workEmailStatus: helperStatus.Neutral,
      employerErrorText: "Employer's name is required",
      initialValue: this.$route.query.name || '',
      isLoading: false,
    };
  },
  computed: {
    automatedCorporateUpgradesEnabled(): boolean {
      return isOn('sc-automate-corporate-upgrades-enabled');
    },
    emailFieldHelperText() {
      if (this.workEmailStatus === helperStatus.Error) {
        if (this.workEmail.length) {
          return 'Enter a valid email';
        }
        return 'Work email is required';
      }
      return 'Enter the email you use at work';
    },
  },
  watch: {
    employerNameInput(value) {
      if (this.employerStatus === helperStatus.Error) {
        if (this.validateEmployerEntered(value)) {
          this.employerStatus = helperStatus.Neutral;
        }
      }
    },
  },
  mounted() {
    this.selectedCompanySlug = this.$route.query.slug;
  },
  methods: {
    closePopup() {
      this.showPopup = false;
    },
    invalidEmployerEntered() {
      sendSegmentEvent('Invalid Employer Entered', {
        employerEntered: this.employerNameInput,
      });
    },
    validateEmployerEntered(employer: string): boolean {
      return employer?.trim().length > 0;
    },
    validateWorkEmail(): boolean {
      const valid = validateEmail(this.workEmail);

      if (!valid) {
        this.workEmailStatus = helperStatus.Error;
      }

      return valid;
    },
    validateEmployerSelection(): boolean {
      const valid = this.automatedCorporateUpgradesEnabled
        ? !!this.selectedCompanySlug?.length
        : this.validateEmployerEntered(this.selectedCompanySlug || this.employerNameInput);

      if (!valid) {
        this.employerStatus = helperStatus.Error;
      }

      return valid;
    },
    async submit() {
      this.employerStatus = helperStatus.Neutral;

      const emailValid = this.validateWorkEmail();
      const employerValid = this.validateEmployerSelection();

      if (emailValid && employerValid) {
        await this.addEmployerMutation();
      }
    },
    filterResults(input) {
      this.employerNameInput = input;
      const searchRegex = new RegExp(input, 'i');
      this.filteredCorporateClients = this.corporateClients.filter((item) => searchRegex.test(item.display));
    },
    updateValue(v): void {
      this.selectedCompanySlug = v.value;
      this.employerNameInput = v.display;
    },
    async submitLegacyEmployerMutation() {
      await this.$apollo.mutate({
        mutation: ADD_EMPLOYER,
        variables: {
          workEmail: this.workEmail,
          employerSlug: this.selectedCompanySlug,
          employerName: this.employerNameInput,
          source: UPGRADE_SOURCE,
        },
      });
    },
    async generateCorporateVerificationToken(): Promise<CorporateVerificationTokenResult> {
      try {
        await this.$apollo.mutate({
          mutation: GENERATE_CORPORATE_VERIFICATION_TOKEN,
          variables: {
            workEmail: this.workEmail,
            employerSlug: this.selectedCompanySlug,
          },
        });
        return 'verification';
      } catch (error) {
        return resolveCorporateVerificationTokenResult(error);
      }
    },
    async addEmployerMutation() {
      this.isLoading = true;
      await this.$nextTick();
      let upgradePath = 'manual';

      try {
        if (this.automatedCorporateUpgradesEnabled) {
          const verificationResult = await this.generateCorporateVerificationToken();

          if (verificationResult === 'verification') {
            upgradePath = 'verification';
          } else if (verificationResult === 'manual_fallback') {
            upgradePath = 'manual_fallback';
          } else {
            await this.submitLegacyEmployerMutation();
            upgradePath = 'manual_fallback';
          }
        } else {
          await this.submitLegacyEmployerMutation();
        }

        sendSegmentEvent('CorporateBenefit_RequestUpgrade', {
          employerSlugEntered: this.selectedCompanySlug,
          employerNameInput: this.employerNameInput,
          source: UPGRADE_SOURCE,
          upgradePath,
        });

        if (upgradePath === 'verification') {
          setItem(STORAGE_KEYS.CORPORATE_UPGRADE_WORK_EMAIL, this.workEmail, 'session');
        } else {
          removeItem(STORAGE_KEYS.CORPORATE_UPGRADE_WORK_EMAIL, 'session');
        }

        this.$router.push({
          name: 'registration-upgrade-request-sent',
          query: {
            redirectPath: '/account/seekers/photo#/upload',
            upgradePath,
          },
        });
      } catch (_error) {
        this.showPopup = true;
      } finally {
        this.isLoading = false;
      }
    },
  },
  apollo: {
    corporateClients: {
      query: CORPORATE_CLIENTS_QUERY,
      result({ data }) {
        const corporateClientsForComboBox = data.corporateClients.map((corporateClient) => {
          return { display: corporateClient.name, value: corporateClient.slug };
        });
        this.corporateClients = corporateClientsForComboBox;

        const selectedClient = this.selectedCompanySlug
          ? corporateClientsForComboBox.find((client) => client.value === this.selectedCompanySlug)
          : null;

        if (selectedClient) {
          this.employerNameInput = selectedClient.display;
          this.initialValue = selectedClient.display;
          this.filteredCorporateClients = [selectedClient];
        } else {
          this.filteredCorporateClients = corporateClientsForComboBox;
        }
      },
    },
  },
};
</script>
